import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { updateApplication } from 'app/frontend/base/api/application'
import { toast } from 'sonner'
import { displayErrorToast } from 'app/frontend/base/helpers'
import { useTranslation } from 'react-i18next'
import ApplicationStatus, {
  applicationStatusColors,
  applicationStatusLabels,
} from 'app/frontend/types/custom/ApplicationStatus'
import { useTeam } from 'app/frontend/base/config/context/team'
import Select from 'app/frontend/base/components/Select'
import { useNavigate, useParams } from 'react-router-dom'
import { useApplicationDetails } from 'app/frontend/base/config/queries'
import { Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'

type Props = {
  inline: boolean
}

const ApplicationStatusActions = ({ inline }: Props) => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { applicationId } = useParams()
  const { data: applicationDetails } = useApplicationDetails(
    team.id,
    applicationId ? parseInt(applicationId) : undefined,
  )

  const [dialogOpen, setDialogOpen] = useState<ApplicationStatus | null>(null)
  const [message, setMessage] = useState('')

  const statusOptions = [
    {
      label: 'Under review',
      value: 'under_review' as ApplicationStatus,
      className: applicationStatusColors['under_review'].text,
      customOptionFn: (_isSelected: boolean) => (
        <div
          className={`flex items-center gap-2 ${applicationStatusColors['under_review'].text}`}
        >
          <span>Under review</span>
        </div>
      ),
    },
    {
      label: 'Accepted',
      value: 'accepted' as ApplicationStatus,
      className: applicationStatusColors['accepted'].text,
      customOptionFn: (_isSelected: boolean) => (
        <div
          className={`flex items-center gap-2 ${applicationStatusColors['accepted'].text}`}
        >
          <span>Accepted</span>
        </div>
      ),
    },
    {
      label: 'Rejected',
      value: 'rejected' as ApplicationStatus,
      className: applicationStatusColors['rejected'].text,
      customOptionFn: (_isSelected: boolean) => (
        <div
          className={`flex items-center gap-2 ${applicationStatusColors['rejected'].text}`}
        >
          <span>Rejected</span>
        </div>
      ),
    },
  ]

  const { mutate: updateStatus, isPending } = useMutation({
    mutationFn: (data: {
      status: ApplicationStatus
      messages_attributes?: { message?: string }[]
    }) => {
      return applicationId
        ? updateApplication<wowaudit.TeamApplication>(
            team.id,
            parseInt(applicationId),
            data,
            'team',
          )
        : Promise.reject(new Error('No application ID found'))
    },
    onSuccess: (updatedApplication) => {
      queryClient.invalidateQueries({
        queryKey: ['teamApplications'],
      })

      // For the open applications count in the subheader
      queryClient.invalidateQueries({
        queryKey: ['team', { path: team.path }],
      })

      if (dialogOpen === 'deleted') {
        toast.success('Application deleted')
        setDialogOpen(null)
        navigate(`${team.path}/applicants`)
      } else {
        if (applicationId) {
          queryClient.setQueryData(
            ['applicationDetails', { id: parseInt(applicationId) }],
            updatedApplication,
          )
        }

        toast.success('Application status updated')
        setDialogOpen(null)
        setMessage('')
      }
    },
    onError: (error) => {
      displayErrorToast(error, t)
    },
  })

  const handleStatusUpdate = () => {
    if (dialogOpen) {
      updateStatus({
        status: dialogOpen,
        messages_attributes: message ? [{ message }] : undefined,
      })
    }
  }

  return (
    <>
      <div
        className={cn(
          'flex justify-end gap-3 items-center',
          inline ? '' : 'pt-4',
        )}
      >
        {inline ? null : (
          <Trash2
            size={20}
            className='text-muted-foreground hover:text-destructive cursor-pointer transition-colors'
            onClick={() => setDialogOpen('deleted')}
          />
        )}

        <Select
          options={statusOptions}
          value={applicationDetails?.status}
          setValue={(value) => {
            if (value && value !== applicationDetails?.status)
              setDialogOpen(value as ApplicationStatus)
          }}
          disabled={isPending}
          placeholder={
            applicationStatusLabels[
              applicationDetails?.status as ApplicationStatus
            ]
          }
          className={
            inline
              ? 'w-full border-0 bg-transparent p-0 h-auto hover:bg-transparent'
              : 'w-40'
          }
        />
      </div>

      <Dialog
        open={!!dialogOpen}
        onOpenChange={(open) => !open && setDialogOpen(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Mark as{' '}
              {applicationStatusLabels[
                dialogOpen as ApplicationStatus
              ]?.toLowerCase()}
            </DialogTitle>
            <DialogDescription>
              This will{' '}
              {dialogOpen === 'under_review'
                ? 'mark the application as under review.'
                : dialogOpen === 'deleted'
                ? 'delete the application.'
                : `${dialogOpen?.slice(0, -2)} the application.`}{' '}
              The applicant will receive a notification automatically, depending
              on their settings.{' '}
              {dialogOpen === 'deleted' && (
                <span className='text-destructive'>This cannot be undone.</span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-2'>
            <label className='text-sm font-medium'>Message (optional)</label>
            <Textarea
              placeholder='Add a message...'
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
            />
          </div>

          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                setDialogOpen(null)
                setMessage('')
              }}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              variant={dialogOpen === 'deleted' ? 'destructive' : 'default'}
              onClick={handleStatusUpdate}
              loading={isPending}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default ApplicationStatusActions
